import { Big } from 'big.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { resolveTierFeeMicros } from '@/common/MarketData';
import type { SizeUnit } from '@/feature/order_book/header/size_unit_toggle';
import { OrderType } from '@/feature/order_entry/order_types';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore';
import { bigIntToBig, bigToString, stringToBig } from '@/utils/bigUtils';
import { FALLBACK_PRICE } from '../constants';
import { useOrderFormContext } from '../order_context';
import type { OrderFormData } from '../order_schema';
import { calculateMaxAllowedNotional, convertQuoteValueToSize, convertSizeToQuoteValue, marketEntryPrice } from '../utils';

// Internal cap to prevent orders from failing due to price fluctuations.
// Slider shows 0–100% but 100% maps to 99% of max allowed notional.
const MAX_ORDER_RATIO = 0.99;

type LastModifiedField = 'size' | 'price' | 'orderSlider' | 'leverage' | 'sizeUnit' | null;

interface UseOrderFormCoordinatorProps {
  priceFieldName?: string;
}

export function useOrderFormCoordinator({ priceFieldName }: UseOrderFormCoordinatorProps = {}) {
  const formContext = useFormContext<OrderFormData>();
  const { accountValue, collateralToken, marketData, feeTier, feeTiersEnabled } = useOrderFormContext();

  // Track which field was last modified by user interaction
  const lastModifiedRef = useRef<LastModifiedField>(null);
  const isUpdatingRef = useRef(false);

  // Watch all relevant fields
  const size = useWatch({ control: formContext.control, name: 'size' });
  const sizeUnit = useWatch({ control: formContext.control, name: 'sizeUnit' });
  const leverage = useWatch({ control: formContext.control, name: 'leverage' });
  const orderType = useWatch({ control: formContext.control, name: 'orderType' });
  const price = useWatch({
    control: formContext.control,
    name: (priceFieldName as keyof OrderFormData) || 'price',
  }) as string;
  const reduceOnly = useWatch({ control: formContext.control, name: 'reduceOnly' });
  const side = useWatch({ control: formContext.control, name: 'side' });
  // Crossed-spread price for market orders — conservative sizing, avoids oversizing vs mark.
  const marketOrderPrice = marketEntryPrice(side, marketData);
  // Core calculations
  const calculations = useMemo(() => {
    const effectivePrice =
      (orderType === OrderType.market ? marketOrderPrice?.toString() : price || marketData?.markPrice?.toString()) ||
      FALLBACK_PRICE;

    const effectivePriceBig = stringToBig(effectivePrice);

    const collateralValue = accountValue && collateralToken ? bigIntToBig(accountValue, collateralToken.decimals) : new Big(0);

    const maxQuoteValue = calculateMaxAllowedNotional(
      collateralValue,
      leverage,
      resolveTierFeeMicros(marketData?.takerFeesMicros, marketData?.takerFeeMicros, feeTier, feeTiersEnabled)
    );

    const convertToQuote = (sizeValue: string, unit: SizeUnit): Big => {
      return convertSizeToQuoteValue(sizeValue, unit, effectivePriceBig);
    };

    const convertFromQuote = (quoteValue: Big, unit: SizeUnit): string => {
      return convertQuoteValueToSize(quoteValue, unit, effectivePriceBig, marketData?.assetDecimals);
    };

    const cappedMaxQuoteValue = maxQuoteValue.times(MAX_ORDER_RATIO);

    const sliderToQuoteValue = (sliderPercent: number): Big => {
      if (reduceOnly) {
        const position = usePositionStore.getState().positions.find((p) => p.marketId === marketData?.id);
        const positionSize = position ? new Big(position.size) : new Big(0);

        if (positionSize.eq(0)) return new Big(0);
        const targetSize = positionSize.times(sliderPercent).div(100);
        return targetSize.times(effectivePriceBig);
      }
      return cappedMaxQuoteValue.times(sliderPercent).div(100);
    };

    const quoteValueToSlider = (quoteValue: Big): number => {
      if (reduceOnly) {
        const position = usePositionStore.getState().positions.find((p) => p.marketId === marketData?.id);
        const positionSize = position ? new Big(position.size) : new Big(0);

        if (positionSize.eq(0) || effectivePriceBig.eq(0)) return 0;
        const size = quoteValue.div(effectivePriceBig);
        const percentage = size.div(positionSize).times(100);
        return Math.min(100, Math.max(0, Math.round(percentage.toNumber())));
      }

      if (cappedMaxQuoteValue.eq(0)) return 0;
      const percentage = quoteValue.div(cappedMaxQuoteValue).times(100);
      return Math.min(100, Math.max(0, Math.round(percentage.toNumber())));
    };

    return {
      effectivePriceBig,
      maxQuoteValue,
      convertToQuote,
      convertFromQuote,
      sliderToQuoteValue,
      quoteValueToSlider,
    };
  }, [
    price,
    orderType,
    marketOrderPrice,
    marketData?.markPrice,
    marketData?.assetDecimals,
    marketData?.id,
    marketData?.takerFeeMicros,
    marketData?.takerFeesMicros,
    feeTier,
    feeTiersEnabled,
    accountValue,
    collateralToken,
    leverage,
    reduceOnly,
  ]);

  // Safe update function that prevents infinite loops
  const safeUpdate = useCallback(
    (updates: Record<string, any>) => {
      if (isUpdatingRef.current) return;

      isUpdatingRef.current = true;
      Object.entries(updates).forEach(([fieldName, value]) => {
        formContext.setValue(fieldName as keyof OrderFormData, value, {
          shouldValidate: false,
          shouldDirty: false,
        });
      });

      // Reset flag after update cycle
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    },
    [formContext]
  );

  // User interaction handlers
  const handleSizeChange = useCallback(
    (newSize: string) => {
      lastModifiedRef.current = 'size';
      const quoteValue = calculations.convertToQuote(newSize, sizeUnit);
      const newSlider = calculations.quoteValueToSlider(quoteValue);
      safeUpdate({ orderSlider: newSlider });
    },
    [calculations, sizeUnit, safeUpdate]
  );

  const handlePriceChange = useCallback(() => {
    lastModifiedRef.current = 'price';
    // When price changes, keep size constant and update slider to reflect new percentage
    const quoteValue = calculations.convertToQuote(size, sizeUnit);
    const newSlider = calculations.quoteValueToSlider(quoteValue);
    safeUpdate({ orderSlider: newSlider });
  }, [calculations, size, sizeUnit, safeUpdate]);

  const handleSliderChange = useCallback(
    (newSlider: number) => {
      lastModifiedRef.current = 'orderSlider';
      if (newSlider === 0) {
        safeUpdate({ size: '' });
        return;
      }
      // 100% reduce-only writes the whole position verbatim (the quote round-trip rounds down and
      // leaves dust); quote sizes round UP — the submit-time reduce-only clamp trims the overshoot.
      if (newSlider === 100 && reduceOnly) {
        const position = usePositionStore.getState().positions.find((p) => p.marketId === marketData?.id);
        if (position && position.size > 0) {
          const size =
            sizeUnit === 'asset'
              ? position.size.toString()
              : bigToString(new Big(position.size).times(calculations.effectivePriceBig), 2, Big.roundUp);
          safeUpdate({ size });
          return;
        }
      }
      const quoteValue = calculations.sliderToQuoteValue(newSlider);
      safeUpdate({ size: calculations.convertFromQuote(quoteValue, sizeUnit) });
    },
    [calculations, sizeUnit, reduceOnly, marketData?.id, safeUpdate]
  );

  const handleLeverageChange = useCallback(() => {
    lastModifiedRef.current = 'leverage';
    // When leverage changes, keep size constant and adjust slider
    const quoteValue = calculations.convertToQuote(size, sizeUnit);
    const newSlider = calculations.quoteValueToSlider(quoteValue);
    safeUpdate({ orderSlider: newSlider });
  }, [calculations, size, sizeUnit, safeUpdate]);

  const handleSizeUnitChange = useCallback(
    (newSizeUnit: SizeUnit) => {
      lastModifiedRef.current = 'sizeUnit';
      // Convert current size to new unit, keep slider percentage the same
      const currentQuoteValue = calculations.convertToQuote(size, sizeUnit);
      safeUpdate({
        sizeUnit: newSizeUnit,
        size: calculations.convertFromQuote(currentQuoteValue, newSizeUnit),
      });
    },
    [calculations, size, sizeUnit, safeUpdate]
  );

  // A slider-set market size tracks a % of buying power, so re-derive it when the balance changes
  // (e.g. a market order partial-fills, IoC-cancels the rest, and settlement lowers the balance) —
  // otherwise a 100% resubmit ships the stale, now-oversized amount and is rejected. Scoped to
  // market orders (the resubmit flow); limit orders rest, so their form size shouldn't move on
  // balance changes. Typed sizes and reduce-only (position-based) sizing are left alone. Keyed on
  // `accountValue` only so it fires on discrete balance events, not every price tick; placed first
  // so it wins the `isUpdatingRef` race against the price effect on a coincident commit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire on balance change only; other values read fresh from the render closure
  useEffect(() => {
    if (isUpdatingRef.current || reduceOnly || orderType !== OrderType.market) return;
    if (lastModifiedRef.current !== 'orderSlider') return;

    const currentSlider = Number(formContext.getValues('orderSlider')) || 0;
    if (currentSlider === 0) return;

    const quoteValue = calculations.sliderToQuoteValue(currentSlider);
    safeUpdate({ size: calculations.convertFromQuote(quoteValue, sizeUnit) });
  }, [accountValue]);

  // Handle external changes (like market price updates)
  useEffect(() => {
    // Only react to external changes if no recent user interaction with price
    if (isUpdatingRef.current || lastModifiedRef.current === 'price') return;

    // For external price changes (like market price updates), maintain size and update slider
    const quoteValue = calculations.convertToQuote(size, sizeUnit);
    const newSlider = calculations.quoteValueToSlider(quoteValue);
    safeUpdate({ orderSlider: newSlider });
  }, [calculations.effectivePriceBig.toString()]); // Convert to string for comparison

  // Handle leverage changes from external sources
  useEffect(() => {
    if (isUpdatingRef.current || lastModifiedRef.current === 'leverage') return;

    // Maintain size when leverage changes externally
    const quoteValue = calculations.convertToQuote(size, sizeUnit);
    const newSlider = calculations.quoteValueToSlider(quoteValue);
    safeUpdate({ orderSlider: newSlider });
  }, [leverage]);

  // Handle reduceOnly toggle
  useEffect(() => {
    if (isUpdatingRef.current) return;

    let quoteValue = calculations.convertToQuote(size, sizeUnit);
    let sizeCapped = false;

    if (reduceOnly) {
      const position = usePositionStore.getState().positions.find((p) => p.marketId === marketData?.id);
      const positionSize = position ? new Big(position.size) : new Big(0);
      const maxQuoteValue = positionSize.times(calculations.effectivePriceBig);

      if (quoteValue.gt(maxQuoteValue)) {
        quoteValue = maxQuoteValue;
        sizeCapped = true;
      }
    }

    const newSlider = calculations.quoteValueToSlider(quoteValue);
    const updates: Record<string, any> = { orderSlider: newSlider };

    if (sizeCapped) {
      updates.size = calculations.convertFromQuote(quoteValue, sizeUnit);
    }

    safeUpdate(updates);
  }, [reduceOnly]);

  return {
    handleSizeChange,
    handlePriceChange,
    handleSliderChange,
    handleLeverageChange,
    handleSizeUnitChange,
    calculations,
  };
}
